import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailTemplateService, EmailTemplate } from '../services/emailTemplateService';
import { createEmailTemplate } from '../services/emailTemplates';
import { db } from '../firebase';
import { collection, doc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import ModernEmailBuilder from './emailBuilder/ModernEmailBuilder';
import AdminTemplateAnalytics from './AdminTemplateAnalytics';
import { EmailTemplate as BuilderTemplate } from '../types/emailBuilder';
import { ensureTemplateHtml } from '../utils/templateHtml';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const AdminEmailTemplateManager: React.FC = () => {
  const { currentUser } = useAuth();
  const { isDark } = useAdminTheme();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [templateCategory, setTemplateCategory] = useState<string>('arts-entertainment');
  const [templateDescription, setTemplateDescription] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'templates' | 'analytics'>('templates');
  const [openTemplateMenu, setOpenTemplateMenu] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [editingDetailsTemplate, setEditingDetailsTemplate] = useState<EmailTemplate | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const isFirstSaveRef = React.useRef(true);
  const lastSavedTemplateIdRef = React.useRef<string | null>(null);
  const isAutoSavingRef = React.useRef(false);
  const lastAutoSaveTimeRef = React.useRef<number>(0);

  useEffect(() => {
    console.log('AdminEmailTemplateManager mounted, currentUser:', currentUser);
    loadTemplates();
  }, [currentUser]);

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.template-card-menu')) {
        setOpenTemplateMenu(null);
      }
    };
    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const allTemplates = await emailTemplateService.getAllTemplates();
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const persistTemplateOrder = useCallback(async (orderedTemplates: EmailTemplate[]) => {
    const updates = orderedTemplates
      .map((template, index) => ({ template, index }))
      .filter(({ template, index }) => template.id && (template.displayOrder ?? index) !== index)
      .map(({ template, index }) =>
        emailTemplateService.updateTemplate(template.id!, { displayOrder: index }).catch((err) =>
          console.error('Failed to update template order', err)
        )
      );

    if (updates.length) {
      await Promise.all(updates);
    }
  }, []);

  const handleTemplateReorder = useCallback(
    (activeId: string, overId: string | null) => {
      if (!overId || activeId === overId) return;
      setTemplates((prev) => {
        const oldIndex = prev.findIndex((t) => t.id === activeId);
        const newIndex = prev.findIndex((t) => t.id === overId);
        if (oldIndex === -1 || newIndex === -1) {
          return prev;
        }
        const reordered = arrayMove(prev, oldIndex, newIndex).map((template, index) => ({
          ...template,
          displayOrder: index,
        }));
        void persistTemplateOrder(reordered);
        return reordered;
      });
    },
    [persistTemplateOrder]
  );


  const handleBuilderSave = async (template: BuilderTemplate) => {
    if (!currentUser) {
      return; // Silent fail for auto-saves
    }

    // Detect if this is an auto-save (happens very quickly after last save)
    const now = Date.now();
    const timeSinceLastSave = now - lastAutoSaveTimeRef.current;
    const isAutoSave = timeSinceLastSave < 3000 || isAutoSavingRef.current; // Less than 3 seconds = auto-save
    
    if (isAutoSave) {
      isAutoSavingRef.current = true;
    }
    
    lastAutoSaveTimeRef.current = now;

    try {
      const htmlContent = await ensureTemplateHtml(template);
      const builderState: BuilderTemplate = { ...template, htmlContent };

      // Check if this is editing an existing template
      const isEditingExisting = editingTemplate && editingTemplate.id;
      
      // Convert BuilderTemplate to EmailTemplate format and save with createdBy: 'admin'
      const templateData = {
        name: template.name || 'Untitled Admin Template',
        subject: template.subject || '',
        htmlContent,
        textContent: template.subject || 'Admin created template',
        category: templateCategory as any, // Use the selected category
        tags: ['admin-created'],
        isActive: true,
        createdBy: 'admin', // This is the key field that makes it visible to all users
        variables: ['first_name', 'last_name', 'email', 'company_name', 'website_url', 'unsubscribe_url'],
        description: templateDescription || 'Admin created template',
        isPaid: false,
        designJson: builderState, // Store the builder template structure
        displayOrder: templates.length,
      };
      
      let templateId: string;
      if (isEditingExisting && editingTemplate.id) {
        // Update existing template silently - don't update React state during auto-save
        // This prevents the builder from re-initializing and causing refresh loops
        await emailTemplateService.updateTemplate(editingTemplate.id, {
          name: templateData.name,
          subject: templateData.subject,
          description: templateData.description,
          htmlContent: templateData.htmlContent,
          textBody: templateData.textContent,
          category: templateData.category,
          tags: templateData.tags,
          isActive: templateData.isActive,
          variables: templateData.variables,
          designJson: builderState,
        });
        templateId = editingTemplate.id;
        // Don't update editingTemplate state or reload templates during auto-save
        // This prevents refresh loop in the builder
      } else {
        // Create new template (first save only - auto-save shouldn't trigger this)
        templateId = await createEmailTemplate(templateData);
        lastSavedTemplateIdRef.current = templateId;
        
        // After first save, set editingTemplate ONCE to enable future updates
        // Only do this if it's not an auto-save to prevent refresh loop
        if (isFirstSaveRef.current && !isAutoSave) {
          isFirstSaveRef.current = false;
          // Create minimal template object WITHOUT designJson to prevent builder re-initialization
          // The builder already has the blocks loaded, we don't want to reload them
          const newEditingTemplate: EmailTemplate = {
            id: templateId,
            name: templateData.name,
            description: templateData.description,
            category: templateData.category,
            previewImage: '',
            htmlContent: templateData.htmlContent,
            tags: templateData.tags,
            uploadedBy: 'admin',
            uploadedAt: new Date(),
            updatedAt: new Date(),
            isActive: true,
            downloadCount: 0,
            displayOrder: templateData.displayOrder,
            // Don't set designJson - let builder keep its current state to prevent re-initialization
          };
          setEditingTemplate(newEditingTemplate);
          // Reload templates list only once after first manual save
          await loadTemplates();
        } else if (isFirstSaveRef.current && isAutoSave) {
          // For auto-save on first save, just mark it but wait for manual save to update state
          isFirstSaveRef.current = false;
        }
      }
      
      // Reset auto-save flag after a delay
      if (isAutoSave) {
        setTimeout(() => {
          isAutoSavingRef.current = false;
        }, 5000);
      }
      
      // Silently save - no alerts, don't close builder, no state updates during auto-save
      // Users can close manually when done
    } catch (error) {
      console.error('Error creating template:', error);
      console.error('Full error object:', error);
      
      // Try to create a minimal template as fallback
      try {
        console.log('Attempting fallback template creation...');
        const fallbackTemplateData = {
          name: 'Fallback Admin Template',
          subject: 'Admin Template',
          htmlContent: '<html><body><h1>Admin Template</h1><p>This is a fallback template.</p></body></html>',
          textContent: 'Admin Template - This is a fallback template.',
          category: templateCategory as any,
          tags: ['admin-created', 'fallback'],
          isActive: true,
          createdBy: 'admin',
          variables: ['first_name', 'last_name', 'email'],
          description: 'Fallback admin template',
          isPaid: false,
          displayOrder: templates.length,
        };
        
        const fallbackTemplateId = await createEmailTemplate(fallbackTemplateData);
        console.log('Fallback template created with ID:', fallbackTemplateId);
        await loadTemplates();
        setShowBuilder(false);
        setEditingTemplate(null);
        alert('Template created successfully using fallback method!');
      } catch (fallbackError) {
        console.error('Fallback template creation also failed:', fallbackError);
        alert(`Error creating template: ${error instanceof Error ? error.message : 'Unknown error'}. Fallback also failed: ${fallbackError instanceof Error ? fallbackError.message : 'Unknown error'}`);
      }
    }
  };

  const handleBuilderCancel = () => {
    setShowBuilder(false);
    setEditingTemplate(null);
    setTemplateDescription(''); // Reset description when canceling
  };

  const navigate = useNavigate();
  
  const handleCreateTemplate = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newTemplateId = doc(collection(db, 'email_templates')).id;
    navigate(`/admin/control-panel-7x9k2m/create-email-templates/${newTemplateId}`);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    if (!template?.id) {
      alert('Please save the template before editing.');
      return;
    }
    navigate(`/admin/control-panel-7x9k2m/email-templates/${template.id}/edit`);
  };

  const handleEditDetails = (template: EmailTemplate) => {
    setEditingDetailsTemplate(template);
    setShowDetailsModal(true);
  };

  const handleSaveDetails = async (updatedTemplate: Partial<EmailTemplate>) => {
    if (!editingDetailsTemplate?.id) return;
    
    try {
      await emailTemplateService.updateTemplate(editingDetailsTemplate.id, updatedTemplate);
      await loadTemplates();
      setShowDetailsModal(false);
      setEditingDetailsTemplate(null);
    } catch (error) {
      console.error('Error updating template details:', error);
      alert('Failed to update template details. Please try again.');
    }
  };


  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      await emailTemplateService.deleteTemplate(id);
      loadTemplates();
    }
  };

  const handleToggleFeatured = async (template: EmailTemplate) => {
    if (!template.id) return;
    
    const currentlyFeatured = templates.filter(t => t.featuredOnDiscoverPage).length;
    const isCurrentlyFeatured = template.featuredOnDiscoverPage || false;
    
    // If trying to feature and already have 10 featured templates
    if (!isCurrentlyFeatured && currentlyFeatured >= 10) {
      alert('You can only feature up to 10 templates on the discover page. Please unfeature another template first.');
      return;
    }
    
    try {
      const newFeaturedStatus = !isCurrentlyFeatured;
      // If featuring, set discoverPageOrder to the next available number
      const updates: Partial<EmailTemplate> = {
        featuredOnDiscoverPage: newFeaturedStatus
      };
      
      if (newFeaturedStatus) {
        const maxOrder = templates
          .filter(t => t.discoverPageOrder !== undefined)
          .reduce((max, t) => Math.max(max, t.discoverPageOrder || 0), -1);
        updates.discoverPageOrder = maxOrder + 1;
      } else {
        updates.discoverPageOrder = undefined;
      }
      
      await emailTemplateService.updateTemplate(template.id, updates);
      await loadTemplates();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to update featured status. Please try again.');
    }
  };

  const handleDuplicateTemplate = async (template: EmailTemplate) => {
    if (!template) {
      return;
    }

    const existingNames = new Set(templates.map((t) => t.name));
    const baseName = template.name ? `${template.name} Copy` : 'Template Copy';
    let duplicateName = baseName;
    let suffix = 2;
    while (existingNames.has(duplicateName)) {
      duplicateName = `${baseName} ${suffix}`;
      suffix += 1;
    }

    let htmlContent = template.htmlContent || '';
    if (!htmlContent) {
      htmlContent = '<!DOCTYPE html><html><body></body></html>';
    }

    const textContent = template.textBody || template.description || template.subject || 'Email template copy';

    try {
      await createEmailTemplate({
        name: duplicateName,
        subject: template.subject || template.name || '',
        htmlContent,
        textContent,
        category: template.category as any,
        tags: template.tags || [],
        isActive: template.isActive !== false,
        createdBy: template.uploadedBy || 'admin',
        variables: template.variables || [],
        description: template.description || '',
        previewImage: template.previewImage,
        isPaid: template.isPaid ?? false,
        price: template.price ?? 0,
        designJson: template.designJson,
        displayOrder: templates.length,
      });

      await loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Failed to duplicate template. Please try again.');
    }
  };

  const handleDeleteAll = async () => {
    if (templates.length === 0) {
      alert('No templates to delete');
      return;
    }

    const confirmMessage = `Are you absolutely sure you want to delete ALL ${templates.length} email templates?\n\nThis action cannot be undone!\n\nType "DELETE ALL" to confirm:`;
    const userInput = window.prompt(confirmMessage);
    
    if (userInput === 'DELETE ALL') {
      try {
        const deletedCount = await emailTemplateService.deleteAllTemplates();
        alert(`Successfully deleted ${deletedCount} template(s)`);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting all templates:', error);
        alert('Failed to delete all templates. Please try again.');
      }
    } else if (userInput !== null) {
      alert('Confirmation text did not match. Deletion cancelled.');
    }
  };

  const handleToggleStatus = async (id: string) => {
    await emailTemplateService.toggleTemplateStatus(id);
    loadTemplates();
  };



  const filteredTemplates = templates.filter(template => {
    const searchLower = searchQuery.toLowerCase().trim();
    const matchesSearch = searchLower === '' || 
                         template.name.toLowerCase().includes(searchLower) ||
                         template.description.toLowerCase().includes(searchLower) ||
                         template.category.toLowerCase().includes(searchLower) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchLower));
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate stats from loaded templates
  const stats = useMemo(() => {
    const activeTemplates = templates.filter(t => t.isActive);
    const byCategory = activeTemplates.reduce((acc, template) => {
      acc[template.category] = (acc[template.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostDownloaded = [...activeTemplates]
      .sort((a, b) => b.downloadCount - a.downloadCount)
      .slice(0, 5);

    return {
      total: activeTemplates.length,
      byCategory,
      mostDownloaded,
    };
  }, [templates]);

  // Define all email template categories with icons and descriptions (alphabetically ordered)
  const categories = [
    { value: 'arts-entertainment', label: 'Arts & Entertainment', icon: '🎨', description: 'Arts and entertainment templates' },
    { value: 'blogs-updates', label: 'Blogs & Updates', icon: '📝', description: 'Blog posts and updates' },
    { value: 'business', label: 'Business', icon: '💼', description: 'Business communication templates' },
    { value: 'deals-offers', label: 'Deals & Offers', icon: '🏷️', description: 'Deals and special offers' },
    { value: 'ecommerce', label: 'E-commerce', icon: '🛒', description: 'Online store emails' },
    { value: 'events', label: 'Events', icon: '🎉', description: 'Event invitations and announcements' },
    { value: 'festive-season', label: 'Festive & Season', icon: '🎄', description: 'Holiday and seasonal templates' },
    { value: 'health-wellness', label: 'Health & Wellness', icon: '💚', description: 'Health and wellness content' },
    { value: 'newsletter', label: 'Newsletter', icon: '📰', description: 'Newsletter templates' },
    { value: 'onboarding', label: 'Onboarding', icon: '🚀', description: 'User onboarding emails' },
    { value: 'professional-services', label: 'Professional Services', icon: '⚖️', description: 'Professional services templates' },
    { value: 'promotional', label: 'Promotional', icon: '📢', description: 'Promotional marketing emails' },
  ];

  const getCategoryCount = (categoryValue: string) => {
    return stats.byCategory[categoryValue] || 0;
  };

  const handleCategoryClick = (categoryValue: string) => {
    setSelectedCategory(categoryValue);
    // Scroll to templates section
    setTimeout(() => {
      const templatesSection = document.getElementById('templates-section');
      if (templatesSection) {
        templatesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const surfaceCardClasses = isDark
    ? 'bg-gray-900/60 border border-gray-800/80 shadow-2xl backdrop-blur-xl'
    : 'bg-white/95 border border-gray-100 shadow-xl backdrop-blur';

  const panelClasses = isDark
    ? 'bg-gray-900/40 border border-gray-800/70 shadow-xl backdrop-blur'
    : 'bg-white/90 border border-gray-100 shadow-lg backdrop-blur';

const BASE_PREVIEW_WIDTH = 640;
const BASE_PREVIEW_HEIGHT = 1400;
const CONTAINER_HEIGHT = 256;
const THUMBNAIL_SIZE = 80;

const TemplatePreviewCard: React.FC<{ template: EmailTemplate; isDark: boolean }> = ({ template, isDark }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.6);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const calculateScale = () => {
      const width = node.clientWidth || 1;
      const nextScale = Math.min(0.9, Math.max(0.45, width / (BASE_PREVIEW_WIDTH + 80)));
      setScale(nextScale);
    };

    calculateScale();

    const observer = new ResizeObserver(() => calculateScale());
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (template.previewImage) {
    return (
      <div
        ref={containerRef}
        className="relative h-64 overflow-hidden flex items-center justify-center bg-gray-950/90"
      >
        <img
          src={template.previewImage}
          alt={template.name}
          className="max-h-full max-w-full object-cover shadow-md"
        />
        <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
      </div>
    );
  }

  if (template.htmlContent) {
    const previewDoc = `
      <style>
        body, html { margin: 0; padding: 0; background: #f8fafc; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }
        body { overflow-y: auto; width: ${BASE_PREVIEW_WIDTH}px; }
        ::-webkit-scrollbar { width: 0; height: 0; }
      </style>
      ${template.htmlContent}
    `;
    const scaledHeight = BASE_PREVIEW_HEIGHT * scale;
    const verticalOffset = Math.max(0, (scaledHeight - CONTAINER_HEIGHT) / 2);

    return (
      <div
        ref={containerRef}
        className="relative h-64 overflow-hidden group/template-preview bg-gray-950/90"
      >
        <iframe
          title={`${template.name} preview`}
          srcDoc={previewDoc}
          className="absolute border-0 shadow-md bg-white"
          style={{
            width: `${BASE_PREVIEW_WIDTH}px`,
            height: `${BASE_PREVIEW_HEIGHT}px`,
            transformOrigin: 'top center',
            transform: `translateX(-50%) scale(${scale})`,
            pointerEvents: 'none',
            left: '50%',
            top: `-${verticalOffset}px`,
          }}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/25 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative h-64 flex flex-col items-center justify-center text-center ${
        isDark ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-black' : 'bg-gradient-to-br from-slate-50 via-white to-blue-50'
      }`}
    >
      <div className="text-4xl mb-3">📨</div>
      <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-600'}`}>Preview not available</p>
      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Upload a preview image or HTML to see it here.</p>
    </div>
  );
};

const TemplatePreviewThumbnail: React.FC<{ template: EmailTemplate; isDark: boolean }> = ({ template, isDark }) => {
  const scale = THUMBNAIL_SIZE / BASE_PREVIEW_WIDTH;

  if (template.previewImage) {
    return (
      <div className="h-20 w-20 overflow-hidden bg-gray-100">
        <img src={template.previewImage} alt={template.name} className="h-full w-full object-cover" />
      </div>
    );
  }

  if (template.htmlContent) {
    const previewDoc = `
      <style>
        body, html { margin: 0; padding: 0; background: #f8fafc; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }
        body { overflow: hidden; width: ${BASE_PREVIEW_WIDTH}px; }
      </style>
      ${template.htmlContent}
    `;
    return (
      <div className={`relative h-20 w-20 overflow-hidden ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <iframe
          title={`${template.name} thumbnail`}
          srcDoc={previewDoc}
          className="absolute border-0"
          style={{
            width: `${BASE_PREVIEW_WIDTH}px`,
            height: `${BASE_PREVIEW_HEIGHT}px`,
            transformOrigin: 'top left',
            transform: `scale(${scale})`,
            pointerEvents: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div className={`h-20 w-20 flex items-center justify-center text-[10px] ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
      No Preview
    </div>
  );
};

interface TemplateCardProps {
  template: EmailTemplate;
  isDark: boolean;
  openTemplateMenu: string | null;
  setOpenTemplateMenu: React.Dispatch<React.SetStateAction<string | null>>;
  onEdit: () => void;
  onEditDetails?: () => void;
  onPreview: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleFeatured?: () => void;
  dragHandleProps?: any;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isDark,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onDuplicate,
  onToggleFeatured,
  dragHandleProps,
}) => {
  return (
    <div
      className={`group relative border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
        isDark ? 'bg-gray-900/80 border-gray-700' : 'bg-white border-gray-100'
      }`}
      data-template-id={template.id}
    >
      <TemplatePreviewCard template={template} isDark={isDark} />
      {template.featuredOnDiscoverPage && (
        <div className="absolute top-2 left-2 z-10">
          <span className="bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-1 rounded-full">
            Featured
          </span>
        </div>
      )}
      <div className="relative p-4 pb-3">
        <div className="flex items-center gap-2 pr-10">
          {dragHandleProps && (
            <button
              type="button"
              {...dragHandleProps}
              className="flex-shrink-0 text-gray-400 hover:text-gray-100 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-500"
              aria-label="Reorder template"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="5" cy="5" r="1.5" />
                <circle cx="5" cy="10" r="1.5" />
                <circle cx="5" cy="15" r="1.5" />
                <circle cx="11" cy="5" r="1.5" />
                <circle cx="11" cy="10" r="1.5" />
                <circle cx="11" cy="15" r="1.5" />
              </svg>
            </button>
          )}
          <h3
            className={`flex-1 text-sm font-semibold tracking-tight truncate ${
              isDark ? 'text-gray-100' : 'text-gray-800'
            }`}
          >
            {template.name}
          </h3>
        </div>
        <TemplateCardActions
          template={template}
          openTemplateMenu={openTemplateMenu}
          setOpenTemplateMenu={setOpenTemplateMenu}
          onEdit={onEdit}
          onEditDetails={onEditDetails}
          onPreview={onPreview}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onToggleFeatured={onToggleFeatured}
          isDark={isDark}
        />
      </div>
    </div>
  );
};

interface TemplateListRowProps extends TemplateCardProps {
  index: number;
}

const TemplateListRow: React.FC<TemplateListRowProps> = ({
  template,
  isDark,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onDuplicate,
  onToggleFeatured,
  index,
  dragHandleProps,
}) => {
  const categoryMeta = categories.find((category) => category.value === template.category);
  const displayCategory = categoryMeta ? `${categoryMeta.icon} ${categoryMeta.label}` : template.category;

  return (
    <div
      className={`relative flex items-center justify-between gap-4 border px-4 py-3 transition-colors ${
        isDark ? 'bg-gray-900/80 border-gray-700' : 'bg-white border-gray-200'
      }`}
      data-template-id={template.id}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {dragHandleProps && (
          <button
            type="button"
            {...dragHandleProps}
            className="flex-shrink-0 text-gray-400 hover:text-gray-100 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-500"
            aria-label="Reorder template"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <circle cx="5" cy="5" r="1.5" />
              <circle cx="5" cy="10" r="1.5" />
              <circle cx="5" cy="15" r="1.5" />
              <circle cx="11" cy="5" r="1.5" />
              <circle cx="11" cy="10" r="1.5" />
              <circle cx="11" cy="15" r="1.5" />
            </svg>
          </button>
        )}
        <TemplatePreviewThumbnail template={template} isDark={isDark} />
        <span
          className={`text-xs font-semibold flex-shrink-0 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          #{index + 1}
        </span>
        <div className="min-w-0 space-y-0.5">
          <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{template.name}</p>
          <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{template.subject || template.description || ''}</p>
          <p className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{displayCategory}</p>
        </div>
      </div>
      <TemplateCardActions
        template={template}
        openTemplateMenu={openTemplateMenu}
        setOpenTemplateMenu={setOpenTemplateMenu}
        onEdit={onEdit}
        onEditDetails={onEditDetails}
        onPreview={onPreview}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onToggleFeatured={onToggleFeatured}
        position="inline"
        isDark={isDark}
      />
    </div>
  );
};

interface TemplateCardActionsProps {
  template: EmailTemplate;
  openTemplateMenu: string | null;
  setOpenTemplateMenu: React.Dispatch<React.SetStateAction<string | null>>;
  onEdit: () => void;
  onEditDetails?: () => void;
  onPreview: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleFeatured?: () => void;
  position?: 'absolute' | 'inline';
  isDark: boolean;
}

const TemplateCardActions: React.FC<TemplateCardActionsProps> = ({
  template,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onDuplicate,
  onToggleFeatured,
  position = 'absolute',
  isDark,
}) => {
  const containerClass =
    position === 'inline'
      ? 'template-card-menu relative ml-auto flex items-center'
      : 'absolute top-3 right-3 template-card-menu';

  return (
    <div className={containerClass}>
      <div className="relative">
        <button
          type="button"
          aria-label="Open template actions"
          onClick={(e) => {
            e.stopPropagation();
            setOpenTemplateMenu((prev) => (prev === template.id ? null : template.id));
          }}
          className="inline-flex h-8 w-8 items-center justify-center text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-100 transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
        {openTemplateMenu === template.id && (
          <div
            className={`absolute right-0 bottom-full mb-2 w-44 rounded-xl border z-50 overflow-hidden ${
              isDark
                ? 'bg-gray-900 border-gray-700 text-gray-100 shadow-2xl'
                : 'bg-white border-gray-200 text-gray-900 shadow-xl'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                isDark ? 'hover:bg-gray-800' : ''
              }`}
              onClick={() => {
                setOpenTemplateMenu(null);
                onEdit();
              }}
            >
              Edit Design
            </button>
            {onEditDetails && (
              <button
                type="button"
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                  isDark ? 'hover:bg-gray-800' : ''
                }`}
                onClick={() => {
                  setOpenTemplateMenu(null);
                  onEditDetails();
                }}
              >
                Edit Details
              </button>
            )}
            <button
              type="button"
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                isDark ? 'hover:bg-gray-800' : ''
              }`}
              onClick={() => {
                setOpenTemplateMenu(null);
                onPreview();
              }}
            >
              Preview
            </button>
            <button
              type="button"
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                isDark ? 'hover:bg-gray-800' : ''
              }`}
              onClick={() => {
                setOpenTemplateMenu(null);
                onDelete();
              }}
            >
              Delete
            </button>
            <button
              type="button"
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                isDark ? 'hover:bg-gray-800' : ''
              }`}
              onClick={() => {
                setOpenTemplateMenu(null);
                onDuplicate();
              }}
            >
              Duplicate
            </button>
            {onToggleFeatured && (
              <button
                type="button"
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                  isDark ? 'hover:bg-gray-800' : ''
                } ${template.featuredOnDiscoverPage ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}
                onClick={() => {
                  setOpenTemplateMenu(null);
                  onToggleFeatured();
                }}
              >
                {template.featuredOnDiscoverPage ? '✓ Featured on Discover' : 'Feature on Discover'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface SortableTemplateCollectionProps {
  templates: EmailTemplate[];
  isDark: boolean;
  openTemplateMenu: string | null;
  setOpenTemplateMenu: React.Dispatch<React.SetStateAction<string | null>>;
  onEdit: (template: EmailTemplate) => void;
  onEditDetails: (template: EmailTemplate) => void;
  onPreview: (templateId?: string) => void;
  onDelete: (templateId?: string) => void;
  onReorder: (activeId: string, overId: string | null) => void;
  onDuplicate: (template: EmailTemplate) => void;
  onToggleFeatured: (template: EmailTemplate) => void;
}

const SortableTemplateCards: React.FC<SortableTemplateCollectionProps> = ({
  templates,
  isDark,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onReorder,
  onDuplicate,
  onToggleFeatured,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over) {
      onReorder(active.id as string, over.id as string);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={templates.map((template) => template.id!).filter(Boolean)} strategy={rectSortingStrategy}>
        <div id="templates-section" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {templates.map((template, index) => (
            <SortableCardItem
              key={template.id ?? `card-${index}`}
              template={template}
              isDark={isDark}
              openTemplateMenu={openTemplateMenu}
              setOpenTemplateMenu={setOpenTemplateMenu}
              onEdit={onEdit}
              onEditDetails={onEditDetails}
              onPreview={onPreview}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onToggleFeatured={onToggleFeatured}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

interface SortableCardItemProps {
  template: EmailTemplate;
  isDark: boolean;
  openTemplateMenu: string | null;
  setOpenTemplateMenu: React.Dispatch<React.SetStateAction<string | null>>;
  onEdit: (template: EmailTemplate) => void;
  onEditDetails: (template: EmailTemplate) => void;
  onPreview: (templateId?: string) => void;
  onDelete: (templateId: string) => void;
  onDuplicate: (template: EmailTemplate) => void;
  onToggleFeatured: (template: EmailTemplate) => void;
}

const SortableCardItem: React.FC<SortableCardItemProps> = ({
  template,
  isDark,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onDuplicate,
  onToggleFeatured,
}) => {
  const fallbackIdRef = useRef(
    template.id ?? `temp-card-${template.name ?? ''}-${Math.random().toString(36).slice(2)}`
  );
  const sortableId = template.id ?? fallbackIdRef.current;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: !template.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform ?? null),
    transition,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TemplateCard
        template={template}
        isDark={isDark}
        openTemplateMenu={openTemplateMenu}
        setOpenTemplateMenu={setOpenTemplateMenu}
        onEdit={() => onEdit(template)}
        onEditDetails={() => onEditDetails(template)}
        onPreview={() => onPreview(template.id)}
        onDelete={() => template.id && onDelete(template.id)}
        onDuplicate={() => onDuplicate(template)}
        onToggleFeatured={() => onToggleFeatured(template)}
        dragHandleProps={listeners}
      />
    </div>
  );
};

const SortableTemplateList: React.FC<SortableTemplateCollectionProps> = ({
  templates,
  isDark,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onReorder,
  onDuplicate,
  onToggleFeatured,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over) {
      onReorder(active.id as string, over.id as string);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={templates.map((template) => template.id!).filter(Boolean)} strategy={verticalListSortingStrategy}>
        <div id="templates-section" className="space-y-3">
          {templates.map((template, index) => (
            <SortableListItem
              key={template.id ?? `list-${index}`}
              template={template}
              index={index}
              isDark={isDark}
              openTemplateMenu={openTemplateMenu}
              setOpenTemplateMenu={setOpenTemplateMenu}
              onEdit={onEdit}
              onEditDetails={onEditDetails}
              onPreview={onPreview}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onToggleFeatured={onToggleFeatured}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

interface SortableListItemProps extends SortableCardItemProps {
  index: number;
}

const SortableListItem: React.FC<SortableListItemProps> = ({
  template,
  index,
  isDark,
  openTemplateMenu,
  setOpenTemplateMenu,
  onEdit,
  onEditDetails,
  onPreview,
  onDelete,
  onDuplicate,
  onToggleFeatured,
}) => {
  const fallbackIdRef = useRef(
    template.id ?? `temp-list-${template.name ?? ''}-${index}-${Math.random().toString(36).slice(2)}`
  );
  const sortableId = template.id ?? fallbackIdRef.current;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: !template.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform ?? null),
    transition,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TemplateListRow
        template={template}
        index={index}
        isDark={isDark}
        openTemplateMenu={openTemplateMenu}
        setOpenTemplateMenu={setOpenTemplateMenu}
        onEdit={() => onEdit(template)}
        onEditDetails={() => onEditDetails(template)}
        onPreview={() => onPreview(template.id)}
        onDelete={() => template.id && onDelete(template.id)}
        onDuplicate={() => onDuplicate(template)}
        onToggleFeatured={() => onToggleFeatured(template)}
        dragHandleProps={listeners}
      />
    </div>
  );
};

  // Memoize the template prop for ModernEmailBuilder to prevent refresh loops
  // This must be at component top level, not conditionally
  const builderTemplate = useMemo(() => {
    if (!editingTemplate) return undefined;
    try {
      // Convert EmailTemplate from emailTemplateService to EmailTemplate for ModernEmailBuilder
      // Use stable reference - only recreate when editingTemplate.id changes
      const designJson = editingTemplate.designJson as BuilderTemplate | undefined;
      return {
        id: editingTemplate.id || '',
        name: editingTemplate.name || 'New Admin Template',
        subject: editingTemplate.subject || editingTemplate.description || '',
        styles: designJson?.styles || {
          containerWidth: '600px',
          backgroundColor: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          contentBackgroundColor: '#ffffff',
          padding: '0px',
          textColor: '#111827'
        },
        htmlContent: editingTemplate.htmlContent || '',
        designJson: designJson,
        createdAt: editingTemplate.uploadedAt ? (editingTemplate.uploadedAt instanceof Date ? editingTemplate.uploadedAt : new Date(editingTemplate.uploadedAt)) : new Date(),
        updatedAt: editingTemplate.updatedAt ? (editingTemplate.updatedAt instanceof Date ? editingTemplate.updatedAt : new Date(editingTemplate.updatedAt)) : new Date()
      };
    } catch (error) {
      console.error('Error converting template:', error);
      return undefined;
    }
  }, [editingTemplate]);

  const openTemplatePreview = useCallback((templateId?: string) => {
    if (!templateId) return;
    window.open(`/admin/control-panel-7x9k2m/email-templates/${templateId}/details`, '_blank', 'noopener,noreferrer');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
          isDark ? 'border-white' : 'border-gray-900'
        }`}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className={`space-y-4 rounded-3xl p-6 ${surfaceCardClasses}`}>
        <div className="flex justify-between items-center">
          <div>
            <h2 className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Email Template Manager</h2>
            <p className={`${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>Upload and manage email templates for all categories</p>
          </div>
          {activeTab === 'templates' && (
            <div className="flex items-center space-x-3">
              <div className={`inline-flex rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-l-lg ${
                    viewMode === 'card'
                      ? isDark
                        ? 'bg-blue-600 text-white'
                        : 'bg-black text-white'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-500 hover:text-gray-900'
                  }`}
                  onClick={() => setViewMode('card')}
                >
          Card
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-r-lg ${
                    viewMode === 'list'
                      ? isDark
                        ? 'bg-blue-600 text-white'
                        : 'bg-black text-white'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-500 hover:text-gray-900'
                  }`}
                  onClick={() => setViewMode('list')}
                >
                  List
                </button>
              </div>
              <button
                type="button"
                onClick={handleCreateTemplate}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 text-center inline-block ${
                  isDark 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-black hover:bg-gray-800 text-white'
                }`}
              >
                Create New Template
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? isDark
                    ? 'border-blue-500 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : isDark
                    ? 'border-transparent text-gray-400 hover:text-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📧 Templates
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? isDark
                    ? 'border-blue-500 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : isDark
                    ? 'border-transparent text-gray-400 hover:text-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Analytics
            </button>
          </nav>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'analytics' ? (
        <AdminTemplateAnalytics />
      ) : (
        <>
          <div className={`space-y-6 rounded-3xl p-6 ${panelClasses}`}>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className={`h-5 w-5 ${
                    isDark ? 'text-gray-500' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search templates by name, description, category, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent ${
                    isDark 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    title="Clear search"
                  >
                    <svg className={`h-5 w-5 ${
                      isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Results Info */}
            {(searchQuery || selectedCategory !== 'all') && (
              <div className={`text-sm ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {filteredTemplates.length === 0 ? (
                  <span>No templates found matching your search criteria.</span>
                ) : (
                  <span>
                    Showing {filteredTemplates.length} of {templates.length} templates
                    {searchQuery && ` matching "${searchQuery}"`}
                    {selectedCategory !== 'all' && ` in ${selectedCategory} category`}
                  </span>
                )}
              </div>
            )}

            {/* Templates Grid */}
          {viewMode === 'card' ? (
            <SortableTemplateCards
              templates={filteredTemplates}
              isDark={isDark}
              openTemplateMenu={openTemplateMenu}
              setOpenTemplateMenu={setOpenTemplateMenu}
              onEdit={handleEditTemplate}
              onEditDetails={handleEditDetails}
              onPreview={openTemplatePreview}
              onDelete={(templateId) => templateId && handleDelete(templateId)}
              onReorder={handleTemplateReorder}
              onDuplicate={handleDuplicateTemplate}
              onToggleFeatured={handleToggleFeatured}
            />
          ) : (
            <SortableTemplateList
              templates={filteredTemplates}
              isDark={isDark}
              openTemplateMenu={openTemplateMenu}
              setOpenTemplateMenu={setOpenTemplateMenu}
              onEdit={handleEditTemplate}
              onEditDetails={handleEditDetails}
              onPreview={openTemplatePreview}
              onDelete={(templateId) => templateId && handleDelete(templateId)}
              onReorder={handleTemplateReorder}
              onDuplicate={handleDuplicateTemplate}
              onToggleFeatured={handleToggleFeatured}
            />
          )}
          </div>
        </>
      )}

      {/* Email Builder */}
      {showBuilder && editingTemplate && (
        <div className={`fixed inset-0 z-50 ${
          isDark ? 'bg-gray-900' : 'bg-white'
        }`}>
          {/* Builder Header with Category Selector */}
          <div className={`border-b ${
            isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-4">
                <h3 className={`text-lg font-semibold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {editingTemplate ? 'Edit Admin Template' : 'Create Admin Template'}
                </h3>
                {/* Category Selector */}
                <div className="flex items-center space-x-2">
                  <label className={`text-sm font-medium ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Category:
                  </label>
                  <select
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="campaign">Campaign</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="welcome">Welcome</option>
                    <option value="transactional">Transactional</option>
                    <option value="automation">Automation</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="marketing">Marketing</option>
                    <option value="social">Social</option>
                    <option value="educational">Educational</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="event">Event</option>
                    <option value="product">Product</option>
                    <option value="announcement">Announcement</option>
                    <option value="follow-up">Follow-up</option>
                    <option value="reminder">Reminder</option>
                    <option value="thank-you">Thank You</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="retention">Retention</option>
                    <option value="real-estate">Real Estate</option>
                    <option value="inspirational">Inspirational</option>
                    <option value="basic">Basic</option>
                  </select>
                </div>
                
                {/* Description Input */}
                <div className="flex items-start space-x-2">
                  <label className={`text-sm font-medium pt-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Description:
                  </label>
                  <textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="Enter template description that will be shown in the template marketplace preview..."
                    rows={3}
                    className={`px-3 py-2 rounded-lg border text-sm min-w-[300px] resize-y ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Selected: {templateCategory}
                </span>
              </div>
            </div>
          </div>
          
          {/* Builder Content */}
          <div className="h-[calc(100vh-80px)]">
            <ModernEmailBuilder
              template={builderTemplate}
              campaignName={editingTemplate?.name || 'New Admin Template'}
              onSave={handleBuilderSave}
              onCancel={handleBuilderCancel}
            />
          </div>
        </div>
      )}

      {/* Edit Details Modal */}
      {showDetailsModal && editingDetailsTemplate && (
        <EditTemplateDetailsModal
          template={editingDetailsTemplate}
          isDark={isDark}
          categories={categories}
          onClose={() => {
            setShowDetailsModal(false);
            setEditingDetailsTemplate(null);
          }}
          onSave={handleSaveDetails}
        />
      )}
    </div>
  );
};

// Edit Template Details Modal Component
interface EditTemplateDetailsModalProps {
  template: EmailTemplate;
  isDark: boolean;
  categories: Array<{ value: string; label: string; icon: string }>;
  onClose: () => void;
  onSave: (updates: Partial<EmailTemplate>) => Promise<void>;
}

const EditTemplateDetailsModal: React.FC<EditTemplateDetailsModalProps> = ({
  template,
  isDark,
  categories,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || '');
  const [category, setCategory] = useState(template.category);
  const [subject, setSubject] = useState(template.subject || '');
  const [tags, setTags] = useState<string[]>(template.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [isActive, setIsActive] = useState<boolean>(template.isActive);
  const [isPaid, setIsPaid] = useState<boolean>(template.isPaid ?? false);
  const [price, setPrice] = useState<string>(
    typeof template.price === 'number' ? template.price.toString() : ''
  );
  const [previewImage, setPreviewImage] = useState<string>(template.previewImage || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        name,
        description,
        category: category as any,
        subject,
        tags,
        isActive,
        isPaid,
        price: isPaid && price !== '' ? Number(price) || 0 : undefined,
        previewImage,
      });
    } catch (error) {
      console.error('Error saving template details:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDark ? 'bg-gray-900' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Edit Template Details
            </h2>
            <button
              onClick={onClose}
              className={`text-gray-500 hover:text-gray-700 ${isDark ? 'hover:text-gray-300' : ''}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Name */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Template Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                  isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Enter template name"
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                  isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Enter template description"
              />
            </div>

            {/* Category */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Category *
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                  isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Email Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                  isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Enter email subject"
              />
            </div>

            {/* Tags */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-gray-500 hover:text-gray-700"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                    isDark
                      ? 'bg-gray-800 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Add a tag and press Enter"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isDark
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  }`}
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Status & Preview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Active Status */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </label>
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className={isDark ? 'text-gray-200 text-sm' : 'text-gray-700 text-sm'}>
                    Template is active
                  </span>
                </label>
              </div>

              {/* Preview Image URL */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Preview Image URL
                </label>
                <input
                  type="text"
                  value={previewImage}
                  onChange={(e) => setPreviewImage(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                    isDark
                      ? 'bg-gray-800 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="https://example.com/preview-image.png"
                />
              </div>
            </div>

            {/* Premium Options */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isPaid}
                    onChange={(e) => setIsPaid(e.target.checked)}
                    className="rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className={isDark ? 'text-gray-200 text-sm font-medium' : 'text-gray-700 text-sm font-medium'}>
                    Premium Template
                  </span>
                </label>

                {isPaid && (
                  <div className="flex items-center space-x-2">
                    <label className={isDark ? 'text-gray-300 text-sm' : 'text-gray-700 text-sm font-medium'}>
                      Price:
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className={`w-24 px-2 py-1 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                        isDark
                          ? 'bg-gray-800 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-6 py-4 border-t flex justify-end space-x-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg font-medium ${
                isDark
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`px-4 py-2 rounded-lg font-medium text-white ${
                isSaving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-black hover:bg-gray-800'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEmailTemplateManager;
